import { useState, useMemo } from "react";
import styles from "./SupplierHealthMonitoring.module.css";
import SelectBox from "../../../components/BaseComponents/SelectBox/SelectBox";
import SupplierHealthDetail from "../../../components/Suppliers/Supplierhealthdetail";
import { useSupplierHealth } from "../../../api/suppliers/useSuppliersApi";

const mapHealthToUi = (s) => ({
  id: s.id || s.supplierId || "",
  name: s.name || s.supplierName || "",
  type: s.type || "API",
  healthStatus: s.healthStatus || s.status || "Online",
  lastApiCall: s.lastApiCall || s.lastApiCallAt || "-",
  lastResult: s.lastResult || s.lastResponse || "Success",
  responseTime: s.responseTime ?? "-",
  uptime: s.uptime ?? 0,
  uptimeTrend: s.uptimeTrend || "up",
  lastError: s.lastError || "-",
});

const SupplierHealthMonitoring = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  const { data: healthRaw, loading, error, refetch } = useSupplierHealth({
    status: statusFilter || undefined,
    search: searchTerm.trim() || undefined,
  });

  const suppliers = useMemo(
    () => (Array.isArray(healthRaw) ? healthRaw.map(mapHealthToUi) : []),
    [healthRaw]
  );

  const totalSuppliers = suppliers.length;
  const onlineCount = suppliers.filter(s => s.healthStatus === "Online").length;
  const degradedCount = suppliers.filter(s => s.healthStatus === "Degraded").length;
  const offlineCount = suppliers.filter(s => s.healthStatus === "Offline").length;

  const filteredSuppliers = suppliers;

  const handleRefresh = () => {
    refetch();
  };

  const handleView = (supplier) => {
    setSelectedSupplier(supplier);
  };

  const handleCloseDrawer = () => {
    setSelectedSupplier(null);
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case "Online": return "✓";
      case "Degraded": return "⚠";
      case "Offline": return "⊗";
      default: return "";
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Supplier Health Monitoring</h1>
          <p className={styles.subtitle}>Real-time operational health view</p>
        </div>
        
        <button className={styles.refreshBtn} onClick={handleRefresh} disabled={loading}>
          <span className={styles.icon}>↻</span>
          Refresh
        </button>
      </div>

      {error && (
        <div
          style={{
            background: "#fef2f2",
            border: "1px solid #fecaca",
            borderRadius: "8px",
            padding: "12px 16px",
            marginBottom: "16px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <span>⚠️</span>
          <span>{error}</span>
        </div>
      )}

      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statHeader}>
            <span className={styles.statLabel}>Total Suppliers</span>
            <span className={styles.statIconNeutral}>⚡</span>
          </div>
          <div className={styles.statValue}>{totalSuppliers}</div>
        </div>

        <div className={`${styles.statCard} ${styles.statCardOnline}`}>
          <div className={styles.statHeader}>
            <span className={styles.statLabel}>Online</span>
            <span className={styles.statIconSuccess}>✓</span>
          </div>
          <div className={styles.statValue}>{onlineCount}</div>
        </div>

        <div className={`${styles.statCard} ${styles.statCardDegraded}`}>
          <div className={styles.statHeader}>
            <span className={styles.statLabel}>Degraded</span>
            <span className={styles.statIconWarning}>⚠</span>
          </div>
          <div className={styles.statValue}>{degradedCount}</div>
        </div>

        <div className={`${styles.statCard} ${styles.statCardOffline}`}>
          <div className={styles.statHeader}>
            <span className={styles.statLabel}>Offline</span>
            <span className={styles.statIconError}>⊗</span>
          </div>
          <div className={styles.statValue}>{offlineCount}</div>
        </div>
      </div>

      <div className={styles.filters}>
        <div className={styles.searchBox}>
          <span className={styles.searchIcon}>🔍</span>
          <input
            type="text"
            placeholder="Search by supplier or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>

        <SelectBox
          placeholder="All Status"
          value={statusFilter}
          options={[
            { label: "All Status", value: "" },
            { label: "Online", value: "Online" },
            { label: "Degraded", value: "Degraded" },
            { label: "Offline", value: "Offline" }
          ]}
          onChange={(e) => setStatusFilter(e.target.value)}
        />
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Supplier</th>
              <th>Type</th>
              <th>Health Status</th>
              <th>Last API Call</th>
              <th>Last Result</th>
              <th>Response Time</th>
              <th>Uptime %</th>
              <th>Last Error</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={9} style={{ textAlign: "center", padding: "24px" }}>
                  Loading supplier health...
                </td>
              </tr>
            ) : filteredSuppliers.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ textAlign: "center", padding: "24px", fontSize: "13px", color: "#6b7280" }}>
                  No data
                </td>
              </tr>
            ) : (
            filteredSuppliers.map((supplier) => (
              <tr key={supplier.id}>
                <td>
                  <div className={styles.supplierInfo}>
                    <div className={styles.supplierName}>{supplier.name}</div>
                    <div className={styles.supplierId}>{supplier.id}</div>
                  </div>
                </td>
                <td>
                  <span className={`${styles.badge} ${styles[`badge${supplier.type}`]}`}>
                    {supplier.type}
                  </span>
                </td>
                <td>
                  <span className={`${styles.healthBadge} ${styles[`health${supplier.healthStatus}`]}`}>
                    <span className={styles.healthIcon}>{getStatusIcon(supplier.healthStatus)}</span>
                    {supplier.healthStatus}
                  </span>
                </td>
                <td className={styles.timeInfo}>
                  <span className={styles.clockIcon}>🕐</span>
                  {supplier.lastApiCall}
                </td>
                <td>
                  <span className={`${styles.resultBadge} ${styles[`result${supplier.lastResult}`]}`}>
                    {supplier.lastResult}
                  </span>
                </td>
                <td className={styles.responseTime}>
                  {supplier.responseTime}
                </td>
                <td>
                  <div className={styles.uptimeCell}>
                    <span className={styles.uptimeValue}>{supplier.uptime}%</span>
                    <span className={`${styles.trendIcon} ${styles[`trend${supplier.uptimeTrend}`]}`}>
                      {supplier.uptimeTrend === "up" ? "↗" : "↘"}
                    </span>
                  </div>
                </td>
                <td className={styles.errorCell}>
                  {supplier.lastError !== "-" ? (
                    <div className={styles.errorMessage}>
                      <span className={styles.errorIcon}>⚠</span>
                      {supplier.lastError}
                    </div>
                  ) : (
                    <span className={styles.noError}>-</span>
                  )}
                </td>
                <td>
                  <button 
                    className={styles.viewBtn}
                    onClick={() => handleView(supplier)}
                    title="View Details"
                  >
                    👁
                  </button>
                </td>
              </tr>
            ))
            )}
          </tbody>
        </table>
      </div>

      {/* Health Detail Drawer */}
      {selectedSupplier && (
        <SupplierHealthDetail
          supplier={selectedSupplier}
          onClose={handleCloseDrawer}
        />
      )}
    </div>
  );
};

export default SupplierHealthMonitoring;